/**
 * Settings → Plan tab.
 *
 * Shows the user's current plan/entitlement and lets them upgrade by selecting
 * a tier, choosing a level (for level-locked tiers), and redeeming a single-use
 * activation code. Replaces the previous "checkout coming soon" placeholder.
 *
 * @module PlanSettingsTab
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { CheckCircle, Loader2, Mail, Copy } from 'lucide-react';
import type { TFunction } from 'i18next';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardDescription,
} from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { logger } from '@/lib/logger';
import { ACCESS_PASSES } from '@/data/accessPasses';
import { redeemPlan } from '@/lib/planApi';
import { useAuth } from '@/hooks/useAuth';
import {
  useCurrentPlan,
  CURRENT_PLAN_QUERY_KEY,
  currentPlanQueryKey,
} from '@/hooks/useCurrentPlan';
import { useEntitlement, ENTITLEMENT_QUERY_KEY } from '@/hooks/useHumanReviews';
import type { Entitlement } from '@/lib/humanReviewsApi';

const CEFR_LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'] as const;

// Tiers that lock the user to a single CEFR level chosen at redemption.
const LEVEL_LOCKED_TIERS = new Set(['starter', 'exam-ready']);

// Tiers that require direct contact instead of self-serve activation.
const CONTACT_TIERS = new Set(['full-prep']);

// Email assembled at runtime to deter scraping bots.
const CONTACT_EMAIL_PARTS = ['deutschgenie', '.', 'contact', '@', 'gmail', '.', 'com'] as const;
function getContactEmail(): string {
  return CONTACT_EMAIL_PARTS.join('');
}

function formatDate(value: string | null): string {
  if (!value) return '';
  try {
    return new Date(value).toLocaleDateString();
  } catch {
    return '';
  }
}

interface HumanReviewQuotaCardProps {
  readonly entitlement: Entitlement;
  readonly t: TFunction;
}

function HumanReviewQuotaCard({ entitlement, t }: HumanReviewQuotaCardProps) {
  const included = Math.max(0, entitlement.includedCount);
  const consumed = Math.max(0, Math.min(entitlement.consumedCount, included));
  const remaining = Math.max(0, entitlement.remaining);
  const hasQuota = included > 0;
  const isExhausted = hasQuota && remaining === 0;
  const usedPct = hasQuota ? Math.min(100, Math.round((consumed / included) * 100)) : 0;

  return (
    <div
      className={cn(
        'rounded-md border p-2.5',
        isExhausted
          ? 'border-destructive/40 bg-destructive/5'
          : 'border-border bg-background',
      )}
    >
      <div className="flex items-center justify-between mb-1.5 gap-1">
        <span
          className={cn(
            'font-ui text-xs font-medium truncate',
            isExhausted ? 'text-muted-foreground' : 'text-foreground',
          )}
        >
          {t('settings.plan.humanReviewsCardLabel', { defaultValue: 'Expert reviews' })}
        </span>
        <span
          className={cn(
            'font-ui text-xs font-semibold shrink-0',
            isExhausted ? 'text-destructive' : hasQuota ? 'text-primary' : 'text-muted-foreground',
          )}
        >
          {hasQuota
            ? isExhausted
              ? t('settings.plan.exhausted', { defaultValue: 'Used up' })
              : t('settings.plan.humanReviewsRemaining', {
                  defaultValue: '{{remaining}} left',
                  remaining,
                })
            : t('settings.plan.humanReviewsNotIncluded', {
                defaultValue: 'Not included',
              })}
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-muted overflow-hidden">
        <div
          className={cn(
            'h-full rounded-full transition-all',
            isExhausted ? 'bg-destructive/60' : 'bg-primary',
          )}
          style={{ width: `${usedPct}%` }}
        />
      </div>
      <p className="font-ui text-xs text-muted-foreground mt-1">
        {hasQuota
          ? `${consumed} / ${included} ${t('settings.plan.attemptsUsed', {
              defaultValue: 'used',
            })}`
          : t('settings.plan.humanReviewsIncluded', {
              defaultValue: '{{included}} included',
              included,
            })}
      </p>
    </div>
  );
}

export function PlanSettingsTab() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { plan, isLoading, refetch } = useCurrentPlan();
  const {
    entitlement,
    isLoading: isEntitlementLoading,
    isError: isEntitlementError,
  } = useEntitlement(!isLoading);

  const [selectedTier, setSelectedTier] = useState<string | null>(null);
  const [selectedLevel, setSelectedLevel] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [isRedeeming, setIsRedeeming] = useState(false);

  const needsLevel = selectedTier ? LEVEL_LOCKED_TIERS.has(selectedTier) : false;
  const isContactTier = selectedTier ? CONTACT_TIERS.has(selectedTier) : false;
  const canSubmit =
    !!selectedTier &&
    !isContactTier &&
    !!code.trim() &&
    (!needsLevel || !!selectedLevel) &&
    !isRedeeming;

  const handleCopyEmail = async () => {
    try {
      await navigator.clipboard.writeText(getContactEmail());
      toast.success(t('settings.plan.fullPrepContactCopied', { defaultValue: 'Email copied!' }));
    } catch {
      // Fallback: open mail client
      window.location.href = `mailto:${getContactEmail()}`;
    }
  };

  const moduleUsageRows =
    plan && plan.mock_quota !== null
      ? plan.limits.allowed_modules.map((module) => ({
          module,
          used: plan.module_usage?.[module] ?? 0,
          quota: plan.mock_quota as number,
        }))
      : [];

  const handleSelectTier = (tierId: string) => {
    setSelectedTier(tierId);
    if (!LEVEL_LOCKED_TIERS.has(tierId)) {
      setSelectedLevel(null);
    }
  };

  const handleRedeem = async () => {
    if (!selectedTier) return;
    setIsRedeeming(true);
    try {
      const result = await redeemPlan({
        tier: selectedTier,
        level: needsLevel ? selectedLevel : null,
        code: code.trim().toUpperCase(),
      });
      toast.success(
        t('settings.plan.activateSuccess', { defaultValue: 'Plan activated successfully' }),
      );
      setCode('');
      setSelectedTier(null);
      setSelectedLevel(null);
      queryClient.setQueryData(currentPlanQueryKey(user?.id), result.plan);
      void queryClient.invalidateQueries({ queryKey: CURRENT_PLAN_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: ENTITLEMENT_QUERY_KEY });
      refetch();
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : t('settings.plan.activateError', { defaultValue: 'Failed to activate plan' });
      logger.error('Plan redemption failed', error, { event: 'settings.plan.redeem.failed' });
      toast.error(message);
    } finally {
      setIsRedeeming(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="font-ui">
          {t('settings.plan.title', { defaultValue: 'Your access pass' })}
        </CardTitle>
        <CardDescription className="font-ui">
          {t('settings.plan.description', {
            defaultValue:
              'Access passes are time-limited full access to the platform — not subscriptions. Pick a pass that fits your exam timeline and activate it with an activation code.',
          })}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Current status */}
        <div className="rounded-lg border border-border bg-muted/40 p-4">
          <p className="font-ui text-sm font-medium text-foreground">
            {t('settings.plan.statusLabel', { defaultValue: 'Current status' })}
          </p>
          {isLoading ? (
            <p className="font-ui mt-1 text-sm text-muted-foreground">
              {t('common.loading', { defaultValue: 'Loading…' })}
            </p>
          ) : (
            <div className="mt-2 space-y-1">
              <div className="flex items-center gap-2">
                <span className="font-ui text-sm font-semibold text-foreground">
                  {plan?.limits.display_name ??
                    t('settings.plan.free', { defaultValue: 'Free' })}
                </span>
                {plan?.is_expired && (
                  <Badge variant="outline" className="font-ui text-destructive border-destructive/50">
                    {t('settings.plan.expired', { defaultValue: 'Expired' })}
                  </Badge>
                )}
              </div>
              {plan?.chosen_level && (
                <p className="font-ui text-sm text-muted-foreground">
                  {t('settings.plan.levelLabel', { defaultValue: 'Level' })}: {plan.chosen_level}
                </p>
              )}
              {plan && plan.mock_quota === null ? (
                <p className="font-ui text-sm text-muted-foreground">
                  {t('settings.plan.unlimitedMocks', { defaultValue: 'Unlimited mock exams' })}
                </p>
              ) : (
                moduleUsageRows.length > 0 && (
                  <div className="mt-3 space-y-2">
                    <p className="font-ui text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      {t('settings.plan.moduleAttemptsTitle', { defaultValue: 'Exam attempts' })}
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      {moduleUsageRows.map((row) => {
                        const remaining = Math.max(0, row.quota - row.used);
                        const isExhausted = remaining === 0;
                        const usedPct = Math.min(100, Math.round((row.used / row.quota) * 100));
                        return (
                          <div
                            key={row.module}
                            className={cn(
                              'rounded-md border p-2.5',
                              isExhausted
                                ? 'border-destructive/40 bg-destructive/5'
                                : 'border-border bg-background',
                            )}
                          >
                            <div className="flex items-center justify-between mb-1.5 gap-1">
                              <span
                                className={cn(
                                  'font-ui text-xs font-medium capitalize truncate',
                                  isExhausted ? 'text-muted-foreground' : 'text-foreground',
                                )}
                              >
                                {t(`moduleSelection.modules.${row.module}.label`, {
                                  defaultValue: row.module,
                                })}
                              </span>
                              <span
                                className={cn(
                                  'font-ui text-xs font-semibold shrink-0',
                                  isExhausted ? 'text-destructive' : 'text-primary',
                                )}
                              >
                                {isExhausted
                                  ? t('settings.plan.exhausted', { defaultValue: 'Used up' })
                                  : t('settings.plan.attemptsRemaining', {
                                      defaultValue: '{{remaining}} left',
                                      remaining,
                                    })}
                              </span>
                            </div>
                            <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                              <div
                                className={cn(
                                  'h-full rounded-full transition-all',
                                  isExhausted ? 'bg-destructive/60' : 'bg-primary',
                                )}
                                style={{ width: `${usedPct}%` }}
                              />
                            </div>
                            <p className="font-ui text-xs text-muted-foreground mt-1">
                              {row.used} / {row.quota}{' '}
                              {t('settings.plan.attemptsUsed', { defaultValue: 'used' })}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )
              )}
              {!isEntitlementLoading && !isEntitlementError && entitlement && (
                <div className="mt-3 space-y-2">
                  <p className="font-ui text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    {t('settings.plan.humanReviewsTitle', { defaultValue: 'Human reviews' })}
                  </p>
                  <HumanReviewQuotaCard entitlement={entitlement} t={t} />
                </div>
              )}
              {plan?.expires_at && !plan.is_expired && (
                <p className="font-ui text-sm text-muted-foreground">
                  {t('settings.plan.expiresOn', { defaultValue: 'Expires on' })}:{' '}
                  {formatDate(plan.expires_at)}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Tier selection */}
        <div>
          <p className="font-ui mb-4 text-sm font-medium text-foreground">
            {t('settings.plan.optionsTitle', { defaultValue: 'Choose a pass to activate' })}
          </p>
          <ul className="grid grid-cols-1 lg:grid-cols-3 gap-4 list-none p-0 m-0 items-stretch">
            {ACCESS_PASSES.map((tier) => {
              const isSelected = selectedTier === tier.id;
              return (
                <li key={tier.id} className="flex">
                  <button
                    type="button"
                    onClick={() => handleSelectTier(tier.id)}
                    aria-pressed={isSelected}
                    className={cn(
                      'flex w-full text-left rounded-lg',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    )}
                  >
                    <Card
                      className={cn(
                        'flex flex-col w-full border-2 transition-all',
                        isSelected
                          ? 'border-primary shadow-md bg-card ring-1 ring-primary'
                          : tier.highlight
                            ? 'border-primary/60'
                            : 'border-border',
                      )}
                    >
                      <CardHeader className="pb-2">
                        <div className="flex items-center gap-2 flex-wrap min-h-[1.5rem] mb-1">
                          {tier.highlight && (
                            <Badge variant="default" className="font-ui bg-primary text-primary-foreground">
                              {t('plans.badge.popular', { defaultValue: 'Most popular' })}
                            </Badge>
                          )}
                          <span className="font-ui text-xs font-medium uppercase tracking-wide text-muted-foreground">
                            {t(tier.positioningKey, { defaultValue: tier.positioningDefault })}
                          </span>
                        </div>
                        <CardTitle className="font-ui text-base font-bold text-foreground">
                          {t(tier.durationLabelKey, { defaultValue: tier.durationDefault })}
                        </CardTitle>
                        <div className="pt-1">
                          <span className="font-ui text-2xl font-bold text-foreground">{tier.price}</span>
                          <span className="font-ui text-muted-foreground text-sm ml-1">
                            {t('plans.oneTime', { defaultValue: 'one-time' })}
                          </span>
                        </div>
                        <CardDescription className="font-ui text-sm text-muted-foreground pt-1">
                          {t(tier.summaryKey, { defaultValue: tier.summaryDefault })}
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="flex-1 pt-1">
                        <ul className="space-y-1.5">
                          {tier.features.map((feature) => (
                            <li key={feature.key} className="flex gap-2 font-ui text-sm text-foreground">
                              <CheckCircle className="h-4 w-4 shrink-0 text-primary mt-0.5" />
                              <span>{t(feature.key, { defaultValue: feature.default })}</span>
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Contact banner — shown when a contact-only tier (e.g. full-prep) is selected */}
        {selectedTier && isContactTier && (
          <div className="rounded-lg border border-primary/40 bg-primary/5 p-5 space-y-3">
            <div className="flex items-start gap-3">
              <Mail className="h-5 w-5 text-primary mt-0.5 shrink-0" />
              <div className="space-y-1">
                <p className="font-ui text-sm font-semibold text-foreground">
                  {t('settings.plan.fullPrepContactTitle', {
                    defaultValue: 'Get your Full Prep Pass',
                  })}
                </p>
                <p className="font-ui text-sm text-muted-foreground">
                  {t('settings.plan.fullPrepContactBody', {
                    defaultValue:
                      "To purchase the Full Prep Pass, please reach out to us directly. We'll send you an activation code once your payment is confirmed.",
                  })}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 pl-8">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleCopyEmail}
                className="gap-2"
              >
                <Copy className="h-3.5 w-3.5" />
                <span className="font-ui select-none" aria-label="Contact email address">
                  {CONTACT_EMAIL_PARTS[0]}
                  {CONTACT_EMAIL_PARTS[1]}
                  {CONTACT_EMAIL_PARTS[2]}
                  {CONTACT_EMAIL_PARTS[3]}
                  {CONTACT_EMAIL_PARTS[4]}
                  {CONTACT_EMAIL_PARTS[5]}
                  {CONTACT_EMAIL_PARTS[6]}
                </span>
              </Button>
              <Button
                type="button"
                size="sm"
                asChild
              >
                <a href={`mailto:${getContactEmail()}`}>
                  {t('settings.plan.fullPrepContactAction', { defaultValue: 'Contact us' })}
                </a>
              </Button>
            </div>
          </div>
        )}

        {/* Activation form (shown once a non-contact tier is selected) */}
        {selectedTier && !isContactTier && (
          <div className="rounded-lg border border-border p-4 space-y-4">
            {needsLevel && (
              <div className="space-y-2">
                <Label className="font-ui">
                  {t('settings.plan.chooseLevel', { defaultValue: 'Choose your level' })}
                </Label>
                <div className="flex flex-wrap gap-2">
                  {CEFR_LEVELS.map((level) => {
                    const isAvailable = level === 'A1';
                    return (
                      <Button
                        key={level}
                        type="button"
                        size="sm"
                        variant={selectedLevel === level ? 'default' : 'outline'}
                        onClick={() => setSelectedLevel(level)}
                        disabled={!isAvailable}
                        title={!isAvailable ? 'Coming soon' : undefined}
                      >
                        {level}
                      </Button>
                    );
                  })}
                </div>
                <p className="font-ui text-xs text-muted-foreground">
                  {t('settings.plan.levelLockNote', {
                    defaultValue: 'This level is locked for the duration of the pass.',
                  })}
                </p>
              </div>
            )}

            <div className="space-y-2 max-w-sm">
              <Label htmlFor="activation-code" className="font-ui">
                {t('settings.plan.codeLabel', { defaultValue: 'Activation code' })}
              </Label>
              <Input
                id="activation-code"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder={t('settings.plan.codePlaceholder', { defaultValue: 'Enter your code' })}
                autoComplete="off"
              />
            </div>

            <Button type="button" onClick={handleRedeem} disabled={!canSubmit}>
              {isRedeeming && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('settings.plan.activate', { defaultValue: 'Activate plan' })}
            </Button>
          </div>
        )}

        <p className="font-ui text-xs text-muted-foreground max-w-xl">
          {t('settings.plan.paymentNote', {
            defaultValue:
              'Online payment is coming soon. For now, activate a pass with an activation code.',
          })}
        </p>
      </CardContent>
    </Card>
  );
}
